import { Popover, IconButton, styled, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { tooltipClasses } from "@mui/material/Tooltip";

// Version for Show on Click
export function InfoToolTipClick({
  msg,
  opacity = 1,
  infoIconSize = 16,
  type = "info",
  testId
}) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleTooltipOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleTooltipClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleCloseTooltip = (e) => {
    e.stopPropagation();
    setOpen(false);
  };

  const tid = testId || "tooltip";

  return (
    <>
      <IconButton
        aria-label={msg}
        size="small"
        style={{ opacity: opacity }}
        onClick={handleTooltipOpen}
        data-testid={`tooltip-trigger-${tid}`}
      >
        {type === "info" && (
          <svg
            width={infoIconSize}
            height={infoIconSize}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14 8A6 6 0 1 1 2 8a6 6 0 0 1 12 0zm2 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM7 5a1 1 0 1 1 2 0 1 1 0 0 1-2 0zm0 3a1 1 0 0 1 2 0v3a1 1 0 1 1-2 0V8z"
              fill="var(--font-heavy-default)"
            />
          </svg>
        )}

        {type === "question" && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 12a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM7.929 3A2.429 2.429 0 0 0 5.5 5.429a1 1 0 1 0 2 0c0-.237.192-.429.429-.429h.45A.62.62 0 0 1 9 5.62a.08.08 0 0 1-.018.051l-1.544 1.93A2 2 0 0 0 7 8.851V9.5a1 1 0 1 0 2 0v-.65l1.544-1.93A2.08 2.08 0 0 0 11 5.62 2.62 2.62 0 0 0 8.38 3h-.451z"
              fill="var(--font-heavy-default)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-6a6 6 0 1 0 0 12A6 6 0 0 0 8 2z"
              fill="var(--font-heavy-default)"
            />
          </svg>
        )}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleTooltipClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        disableRestoreFocus
        disableScrollLock
        PaperProps={{
          style: {
            maxWidth: 250,
            borderRadius: "8px",
            boxShadow:
              "0 2px 8px 0 rgba(0, 0, 0, 0.08), 0 -4px 20px 0 rgba(0, 0, 0, 0.08)",
            border: "solid 1px var(--new-border)",
            backgroundColor: "var(--app-body)",
            color: "var(--font-medium-default)",
            fontSize: "14px",
            fontWeight: "400",
            minWidth: "300px",
            transform: "translateY(-40px)"
          }
        }}
      >
        <div style={{ textAlign: "right" }}>
          <IconButton size="small" onClick={handleCloseTooltip} data-testid={`tooltip-close-${tid}`}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.793 12.207a1 1 0 0 0 1.414-1.414L9.414 8l2.793-2.793a1 1 0 0 0-1.414-1.414L8 6.586 5.207 3.793a1 1 0 0 0-1.414 1.414L6.586 8l-2.793 2.793a1 1 0 1 0 1.414 1.414L8 9.414l2.793 2.793z"
                fill="var(--font-heavy-default)"
              />
            </svg>
          </IconButton>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0px 16px 16px 16px"
          }}
        >
          <div data-testid={`tooltip-text-${tid}`}>{msg}</div>
        </div>
      </Popover>
    </>
  );
}

// Version for Show on Hover
export function InfoToolTipHover({
  msg,
  opacity = 1,
  infoIconSize = 16,
  type = "info",
  testId
}) {
  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} enterTouchDelay={0} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "var(--app-body)",
      color: "var(--new-foreground)",
      maxWidth: 250,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid var(--new-border)"
    }
  }));

  const tid = testId || "tooltip";

  return (
    <HtmlTooltip title={msg} data-testid={`tooltip-${tid}`}>
      <IconButton
        aria-label={msg}
        size="small"
        style={{
          opacity: opacity
        }}
        data-testid={`tooltip-trigger-${tid}`}
      >
        <svg
          width={infoIconSize}
          height={infoIconSize}
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14 8A6 6 0 1 1 2 8a6 6 0 0 1 12 0zm2 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM7 5a1 1 0 1 1 2 0 1 1 0 0 1-2 0zm0 3a1 1 0 0 1 2 0v3a1 1 0 1 1-2 0V8z"
            fill="var(--future-tax-140)"
          />
        </svg>
      </IconButton>
    </HtmlTooltip>
  );
}
